/**
 * Per-site stylesheets on the client (see sync-server/site-css/README.md).
 *
 * A feed whose site has a checked-in stylesheet carries its content hash as `siteCss`. The
 * stylesheet is scoped to the class `feed-<domain>` (dots as underscores), which
 * createPostElement puts on every post card, and it is loaded from the CDN as a `<link>` — once per
 * domain and hash per page load; a set remembers what is already in the document, the browser's
 * HTTP cache does the rest (the path is immutable).
 */

import { siteDomain, siteCssClass, siteCssPath } from '../schema-copy/site-css';
import { feedCdnBaseUrl } from '../schema-copy/feed-cdn';

const loaded = new Set<string>();

/** The `feed-<domain>` class for a post of the given site, or undefined when the URL has no host. */
export function postSiteClass(siteUrl: string | undefined): string | undefined {
    const domain = siteDomain(siteUrl);
    return domain ? siteCssClass(domain) : undefined;
}

/**
 * Make sure the site stylesheet of `feed` (if it has one) is in the document. Cheap to call on
 * every post render. A hash that changed since the last call appends a second link; its rules
 * come later in the cascade and win at equal specificity, which is what a newer version wants.
 */
export function ensureSiteCss(feed: { id: string; siteUrl?: string; siteCss?: string | null }): void {
    const domain = feed.siteCss ? siteDomain(feed.siteUrl) : undefined;
    if (!domain) return;
    const path = siteCssPath(domain, feed.siteCss!);
    if (loaded.has(path)) return;
    loaded.add(path);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = feedCdnBaseUrl(feed.id) + path;
    link.dataset.siteCss = domain;
    document.head.appendChild(link);
}

/** @internal Forget what was loaded (tests). Does not remove the links. */
export function _resetSiteCssForTesting(): void {
    loaded.clear();
}
